import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';

// A dedicated instance, so the hook below applies to banner messages only and
// not to any other `DOMPurify.sanitize` call in the app.
const purify = DOMPurify(window);

// Every link opens in a new tab: a banner link that navigated the current tab
// would pull the user off whatever they were watching. DOMPurify strips
// `target` by default, and this runs after that, so the attributes survive.
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

type Props = {
  html: string;
};

/**
 * Renders operator-authored HTML from a LaunchDarkly flag. Sanitized because
 * anyone with edit rights in LaunchDarkly can set the value, and it reaches
 * every session. Plain text passes through unchanged, so messages written
 * before HTML was supported render exactly as they did.
 */
const HtmlMessage = ({ html }: Props) => {
  const __html = useMemo(() => purify.sanitize(html), [html]);

  return (
    <Box
      sx={{
        // There is no global link style, so a bare <a> would otherwise render in
        // the browser's default blue against the Alert's tinted background.
        '& a': { color: 'inherit' },
        // A <p> brings its own 1em margins, which would pad the banner beyond
        // the Alert's own. Interior spacing between paragraphs is kept.
        '& > :first-child': { mt: 0 },
        '& > :last-child': { mb: 0 },
      }}
      dangerouslySetInnerHTML={{ __html }}
    />
  );
};

export default HtmlMessage;
